// HeatMapEmpreendimentos.tsx
import React, { useState, useEffect } from 'react';
import Map, { Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { LayerProps } from 'react-map-gl';
import { Imovel } from './MapaEmpreendimentos';

export interface MunicipioFeature {
  type: 'Feature';
  properties: Record<string, any>;
  geometry: {
    type: string;
    coordinates: any[];
  };
}

// Camada de cor nos setores
const choroplethLayer: LayerProps = {
  id: 'setores-choropleth',
  type: 'fill',
  source: 'setores',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['get', 'qtdEmpreendimentos'],
      0, '#B0E5FF',
      1, '#32BEF0',
      3, '#2A86E3',
      5, '#4F65D6',
      10, '#29306A'
    ],
    'fill-opacity': 0.7,
    'fill-outline-color': '#333'
  }
};

// Camada de pontos
const pontosLayer: LayerProps = {
  id: 'pontos-empreendimentos',
  type: 'circle',
  source: 'pontos',
  paint: {
    'circle-radius': 6,
    'circle-color': '#1f234f',
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff'
  }
};

interface HeatMapEmpreendimentosProps {
  initialViewState?: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  imoveis: Imovel[];
  municipios?: MunicipioFeature[]; // geometria passada via props
}

export default function HeatMapEmpreendimentos({
  initialViewState,
  imoveis,
  municipios
}: HeatMapEmpreendimentosProps) {
  const [viewState, setViewState] = useState({
    latitude: initialViewState?.latitude || -8.05,
    longitude: initialViewState?.longitude || -34.9,
    zoom: initialViewState?.zoom || 11,
  });

  const [geojson, setGeojson] = useState<any>(null);
  const [pontosGeojson, setPontosGeojson] = useState<any>(null);

  // Atualiza cores e pontos com os imóveis recebidos
  useEffect(() => {
    if (!municipios) return;

    // 1. Converte pontos em GeoJSON
    const pontosGeojsonData = {
      type: 'FeatureCollection',
      features: imoveis.map(emp => ({
        type: 'Feature',
        properties: { id: emp.id, bairro: emp.bairro },
        geometry: { type: 'Point', coordinates: [emp.longitude, emp.latitude] }
      }))
    };
    setPontosGeojson(pontosGeojsonData);

    // 2. Conta empreendimentos por bairro
    function normalize(str: string) {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    const contagemPorBairro: Record<string, number> = {};
    imoveis.forEach(emp => {
      if (emp.bairro) {
        const nome = normalize(emp.bairro);
        contagemPorBairro[nome] = (contagemPorBairro[nome] || 0) + 1;
      }
    });

    // 3. Cria GeoJSON atualizado para colorir os bairros
    const updatedGeojson = {
      type: 'FeatureCollection',
      features: municipios.map((feature: MunicipioFeature) => {
        const bairroNome = normalize(feature.properties['nome'] || '');
        const qtd = contagemPorBairro[bairroNome] || 0;
        return {
          ...feature,
          properties: {
            ...feature.properties,
            qtdEmpreendimentos: qtd
          }
        };
      })
    };

    setGeojson(updatedGeojson);
  }, [municipios, imoveis]);
  
  useEffect(() => {
    return () => {
      // Cleanup: remove listeners e referências de mapa
      setGeojson(null);
      setPontosGeojson(null);
    };
  }, []);

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        maxWidth: '97.5%',
        marginLeft: 'auto',
        marginRight: 'auto',
        borderRadius: '12px',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Map
        key="heatmap"
        {...viewState}
        mapboxAccessToken="pk.eyJ1IjoiZXZhbmRzb25jZXNhciIsImEiOiJjbWN3YXNtMjkwMGl2Mm5wd21mdGRlazA0In0.bvgSecEoO0w-PDKVfkcpjQ"
        mapStyle="mapbox://styles/mapbox/light-v10"
        onMove={evt => setViewState(evt.viewState)}
        style={{ width: '100%', height: '100%' }}
      >
        {geojson && (
          <Source id="setores" type="geojson" data={geojson}>
            <Layer {...choroplethLayer} />
          </Source>
        )}

        {pontosGeojson && (
          <Source id="pontos" type="geojson" data={pontosGeojson}>
            <Layer {...pontosLayer} />
          </Source>
        )}
      </Map>
    </div>
  );
}
